import { dataService } from '../../data';
import { AppConstants } from '@unpispas/upp-defs';

import { DataObject, ObjectOptions, RelatedObject } from "../base";
import { CreateObject } from "../item";
import { QrCodeView, QrCodeViewProxy } from '../views/qrcode';
import { QrScan } from './qrscan';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { PlaceType } from '../types';
import { QrScanType } from '../types';
import { FloorAreaType } from '../types';
import { DrawItemType } from '../types';
import { TicketType } from '../types';
import { TillType } from '../types';
import { PriceZoneType } from '../types';
import { AskWaiterType } from '../types';

import { Place } from './place';

const _schema = {
    "name": "QRCODE",
    "fields": [
        { name: "place", type: "objid" },
        { name: "qrscan", type: "objid" },
        { name: "status", type: "string", default: "AC" },
        { name: "area", type: "objid" },
        { name: "alias", type: "string" },
        { name: "number", type: "number" },
        { name: "chairs", type: "number", default: 1 },
        { name: "draw", type: "objid" },
        { name: "till", type: "objid" },
        { name: "pricezone", type: "objid" }
    ],
    relate: [
        { direction: ">", target: "PLACE", by: "place", name: "place", reason: "place", child: false, class: PlaceType },
        { direction: ">", target: "QRSCAN", by: "qrcode", name: "qrscan", reason: "qrscan", child: false, class: QrScanType },
        { direction: ">", target: "FLOORAREA", by: "area", name: "area", reason: "area", child: true, class: FloorAreaType },
        { direction: ">", target: "DRAWITEM", by: "draw", name: "draw", reason: "draw", child: true, class: DrawItemType },
        { direction: ">", target: "TILL", by: "till", name: "till", reason: "till", child: false, class: TillType },
        { direction: ">", target: "PRICEZONE", by: "pricezone", name: "pricezone", reason: "pricezone", child: false, class: PriceZoneType },
        { direction: "<", target: "ASKWAITER", by: "qrcode", name: "request", reason: "requests", child: true, class: AskWaiterType },
        { direction: "<", target: "EXTRATABLE", by: "qrcode", name: "extra", reason: "extras", child: true },
        { direction: "<", target: "TICKET", by: "qrcode", name: "ticket", reason: "tickets", child: true, class: TicketType }
    ]
} as const;

export const UPP_TABLE_CHAIRS_MIN = 1;
export const UPP_TABLE_CHAIRS_MAX = 99;

const _QrCodeClass = CreateObject(_schema);
if (!_QrCodeClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class QrCode extends _QrCodeClass {
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): QrCode {
        return this._Copy(store) as QrCode;
    }

    /**
     * Completes the 1:1 with the global scan without marking `QRSCAN` to commit.
     * `SetChild('qrscan', scan, 'qrcode')` notifies this hook with `by` (`qrcode`).
     * Concat of a QRCODE row would otherwise leave `scan.qrcode` empty.
     * Same idea as `SetChildren` reversing a 1:N FK.
     *
     * Changelog rows also lack `PLACE.qrcodes`; register on `place` link.
     */
    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf){
            return;
        }

        if (relation === 'qrcode' && related instanceof QrScan){
            if (related === this.qrscan){
                if (related.qrcode !== this){
                    related.SetChild('qrcode', this, 'qrcode');
                    related.ToUpdate = false;
                }
            }
            else if (related.qrcode === this){
                related.SetChild('qrcode', null, 'qrcode');
                related.ToUpdate = false;
            }
            return;
        }

        if (relation === 'place' && related instanceof Place){
            if (related === this.place){
                related.AddQrcode(this);
            }
            else {
                related.DelQrcode(this);
            }
        }
    }

    override get viewProxy(): QrCodeView {
        return QrCodeViewProxy(this, this.data);
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get status(): string | null {
        return this['__base__status'];
    }

    set status(value: string){
        if (this.status == 'DE'){
            return;     // cannot modify deleted items
        }

        if (this.status == value){
            return;
        }

        this['__base__status'] = value;
        if (this.place){
            if ((this.status == 'DE') && !this.Commited){
                this.place.DelQrcode(this);
            }
            else {
                this.place.DoRefresh(this.table);
            }
        }
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get IsValid(): boolean {
        return (!!this.status && (this.status != 'DE'));
    }

    get IsPending(): boolean {
        return (this.status == 'PA');
    }

    /**
     * Public guest sticker URL (`qraURL` + query). Never POS `thisURL` and never the v3 `qr-access` path.
     * @param language Language code; stored uppercase in the query.
     * @returns Absolute guest URL, or null when the table cannot be printed.
     */
    QrCodeUrl(language: string): string | null {
        if (!this.IsValid || this.IsPending || !this.qrscan || !this.qrscan.objid || !this.qrscan.authorization){
            return null;
        }

        const _base = String(AppConstants.qraURL || '');
        const _root = _base.endsWith('/') ? _base : (_base + '/');

        const _lang = String(language || '').toUpperCase();
        return _root
            + "?qr=true"
            + "&lang=" + encodeURIComponent(_lang)
            + "&validQR=" + encodeURIComponent(String(this.qrscan.authorization))
            + "&table=" + encodeURIComponent(String(this.qrscan.objid));
    }
}
